import { readAnalyticsConsent } from "@/lib/consent";
import { disableClarity, enableClarity } from "@/lib/clarity";

declare global {
  interface Window {
    __isSuperAdmin?: boolean;
    /** Set by gtag-init.js when it skipped gtag('config') for a flagged browser. */
    __gaConfigSkipped?: boolean;
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Super-admin analytics opt-out.
 *
 * Super admins are internal accounts whose traffic must never reach Vercel
 * Web Analytics, Speed Insights, GA4 or Microsoft Clarity. SuperAdminAnalyticsSync
 * persists the flag once the profile resolves; public/gtag-init.js reads it
 * BEFORE React boots (the localStorage key literal is duplicated there, same
 * precedent as exos_analytics_consent) so GA4's config call is skipped, and both
 * the Vercel `beforeSend` guards and CookieConsent's Clarity gate see
 * `window.__isSuperAdmin` from the first beacon.
 */
export const SUPER_ADMIN_OPT_OUT_KEY = "exos_super_admin";
/** Duplicated in index.html and public/gtag-init.js (static, no imports). */
export const GA_MEASUREMENT_ID = "G-NVGRVHZCWE";

/** True when analytics must be dropped for this browser (super admin). */
export function isAnalyticsSuppressed(): boolean {
  if (typeof window === "undefined") return false;
  if (window.__isSuperAdmin === true) return true;
  // Fallback for the rare case gtag-init.js never ran (blocked by an
  // extension) and React hasn't set the window mirror yet.
  try {
    return localStorage.getItem(SUPER_ADMIN_OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

/** Idempotent: suppress all analytics for this browser (super admin). */
export function applySuperAdminOptOut(): void {
  window.__isSuperAdmin = true;
  // Google-documented kill switch — stops GA4 hits even when gtag('config')
  // already ran earlier this session (first-detection case).
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  window.gtag?.("consent", "update", { analytics_storage: "denied" });
  // Clarity has no kill switch, so stop it outright and bin its cookies.
  disableClarity();
  try {
    localStorage.setItem(SUPER_ADMIN_OPT_OUT_KEY, "1");
  } catch {
    /* localStorage blocked — window flag + ga-disable still suppress this session */
  }
}

/**
 * Resume normal tracking (a signed-in non-super-admin owns this browser).
 * Undoes everything the opt-out did this session: restores the browser's
 * stored human consent choice (applySuperAdminOptOut revoked it) and replays
 * the gtag('js')/gtag('config') that gtag-init.js skipped at boot — without
 * the replay, GA4 would stay dead for the regular user until the next full
 * page load. The late config is consent-safe: the Consent Mode "denied"
 * default was queued unconditionally at boot.
 */
export function clearSuperAdminOptOut(): void {
  window.__isSuperAdmin = false;
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
  try {
    localStorage.removeItem(SUPER_ADMIN_OPT_OUT_KEY);
  } catch {
    /* localStorage blocked — nothing persisted to clear */
  }
  if (typeof window.gtag === "function") {
    if (readAnalyticsConsent()) {
      window.gtag("consent", "update", { analytics_storage: "granted" });
    }
    if (window.__gaConfigSkipped) {
      window.__gaConfigSkipped = false;
      window.gtag("js", new Date());
      window.gtag("config", GA_MEASUREMENT_ID);
    }
  }
  // Same idea for Clarity: CookieConsent ran earlier this page load and skipped
  // it while the flag was set, so start it here rather than a load later.
  if (readAnalyticsConsent()) {
    enableClarity();
  }
}

/**
 * Shared URL sanitizer for Vercel Web Analytics + Speed Insights `beforeSend`.
 *
 * - Drops internal /admin traffic so it never pollutes product analytics.
 * - Normalizes high-cardinality dynamic routes to stable patterns
 *   (e.g. /projects/abc123 -> /projects/[projectId]) for cleaner aggregation.
 * - Strips query string and hash, which can carry reset/share tokens or PII.
 * - Returns an ABSOLUTE URL (origin preserved): Vercel's intake script parses
 *   the value with `new URL(...)` to derive the event origin, so returning a
 *   bare path makes `new URL()` throw and the beacon is silently dropped —
 *   which is what produces a permanently empty analytics dashboard.
 *
 * Returns the sanitized absolute URL, or `null` to drop the event entirely.
 */
export function redactAnalyticsUrl(rawUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    // Vercel always passes an absolute URL; fall back to the page origin so we
    // still return a valid absolute URL the intake can parse.
    try {
      url = new URL(rawUrl, typeof window !== "undefined" ? window.location.origin : undefined);
    } catch {
      return null;
    }
  }

  if (url.pathname.startsWith("/admin")) return null;

  url.pathname = url.pathname
    .replace(/^\/report\/[^/]+$/, "/report/[shareId]")
    .replace(/^\/report-library\/[^/]+$/, "/report-library/[reportId]")
    .replace(/^\/projects\/[^/]+$/, "/projects/[projectId]")
    .replace(/^\/analyse\/[^/]+$/, "/analyse/[scenarioSlug]")
    .replace(/^\/blog\/[^/]+$/, "/blog/[slug]")
    .replace(/^\/enterprise\/risk\/monitor\/[^/]+$/, "/enterprise/risk/monitor/[id]")
    .replace(/^\/enterprise\/inflation\/tracker\/[^/]+$/, "/enterprise/inflation/tracker/[id]");
  url.search = "";
  url.hash = "";

  return url.toString();
}
